import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import ChevronLeft from 'lucide-react-native/icons/chevron-left';
import ChevronRight from 'lucide-react-native/icons/chevron-right';
import { useRef } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { navHref } from '@/components/riviera/nav-links';
import { BoatCard } from '@/components/riviera/boat-card';
import { Fonts, Spacing } from '@/constants/theme';
import { useFilters } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { useLocale } from '@todo/core/i18n/use-locale';
import { useBoats } from '@todo/core/hooks/use-boats';
import { cities } from '@/lib/events-data';
import { BOATS_INDEX_QUERY, BOATS_INDEX_SEED_KEY } from '@todo/core/list-queries';

import css from './carousel-section.module.css';

/** How many cards a carousel renders — the seeded list holds more than this. */
const CAROUSEL_BOATS_LIMIT = 10;

// Web-only mirror of experiences-carousel.web.tsx, adapted for boats.
export function BoatsCarousel() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { activeCity } = useFilters();
  const isDefaultView = activeCity === 'all';
  const { boats: allBoats, loading, error } = useBoats(
    isDefaultView ? BOATS_INDEX_QUERY : { city: activeCity },
    isDefaultView ? BOATS_INDEX_SEED_KEY : undefined,
  );
  const boats = allBoats.slice(0, CAROUSEL_BOATS_LIMIT);
  const cityName = cities.find((c) => c.id === activeCity)?.name;
  const heading =
    activeCity !== 'all' && cityName
      ? t('boats.headingCity', { city: cityName })
      : t('boats.headingRegion');

  if (!loading && !error && boats.length === 0) return null;

  const scrollBy = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <div className={css.section}>
      <div className={css.heading}>
        <h2 className={css.headingText} style={{ color: theme.text, fontFamily: Fonts?.serif }}>
          {heading}
        </h2>
        <Link href={navHref('/[lang]/boats', locale)} asChild>
          <Pressable accessibilityRole="link" style={styles.seeAll}>
            {({ pressed }) => (
              <>
                <Text
                  style={[
                    styles.seeAllText,
                    { color: theme.primary },
                    pressed && { opacity: 0.6 },
                  ]}>
                  {t('common.seeAll')}
                </Text>
                <ArrowRight
                  size={16}
                  color={theme.primary}
                  style={pressed && { opacity: 0.6 }}
                />
              </>
            )}
          </Pressable>
        </Link>
      </div>
      <div className={css.carouselWrap}>
        <div ref={scrollRef} className={css.scroller}>
          {loading ? (
            <Text style={{ color: theme.mutedForeground, paddingHorizontal: Spacing.three }}>
              {t('boats.loading')}
            </Text>
          ) : error ? (
            <Text style={{ color: theme.destructive, paddingHorizontal: Spacing.three }}>
              {error}
            </Text>
          ) : (
            boats.map((boat) => <BoatCard key={boat.id} boat={boat} />)
          )}
        </div>
        <div className={css.arrows}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('a11y.previous')}
            onPress={() => scrollBy(-1)}
            style={({ pressed }) => [
              styles.navBtn,
              styles.navBtnLeft,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.8 },
            ]}>
            <ChevronLeft size={20} color={theme.text} />
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('a11y.next')}
            onPress={() => scrollBy(1)}
            style={({ pressed }) => [
              styles.navBtn,
              styles.navBtnRight,
              { backgroundColor: theme.card, borderColor: theme.border },
              pressed && { opacity: 0.8 },
            ]}>
            <ChevronRight size={20} color={theme.text} />
          </Pressable>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 13,
    fontWeight: '600',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBtnLeft: {
    left: -12,
  },
  navBtnRight: {
    right: -12,
  },
});
